import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter,
  Input, OnInit, Output, ViewChild
} from '@angular/core';
import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { AccessibilityService } from './../../services/accessibility.service';
import { InputType, KeyboardService } from './../../services/keyboard.service';
import { BaseComponent } from './../base/base.component';

@Component({
  providers: [KeyboardService],
  selector: 'app-textbox',
  styleUrls: ['./textbox.component.scss'],
  templateUrl: './textbox.component.html'
})
export class TextboxComponent extends BaseComponent implements OnInit, AfterViewInit {
  inputWidth: string;


  hideBottomBorder = false;

  isValid = true;

  /** @ViewChild
   * @param {ElementRef} elePrefix - prefix element instance;
   */
  @ViewChild('text') eleTextBox: ElementRef;


  /** @ViewChild
   * @param {ElementRef} eleEditable - input type element instance.
   */
  @ViewChild('ele_editable') eleEditable: ElementRef;

  /** @ViewChild
   * @param {ElementRef} eleEditable - input type element instance.
   */
  @ViewChild('prefix_label') elePrefix: ElementRef;


  /** @customClass - set class attribute value from outside
   */
  @Input() customClass: string;

  /** @textboxId - set id attribute value from outside
 */
  @Input() textboxId: string;


  @Input() maxChar: number;
  /** @focusOnPrefixClicked - set focus on Editable element ( eleEditable ) when prefix button is clicked.
   */
  @Input() focusOnPrefixClicked: boolean;

  /** @input_type - type attribute of input tag i.e. number or text.
   */
  @Input() inputType: InputType;

  @Input() get labelledBy(): string {
    if (this.inputId !== undefined) {
      return this.inputId.toString().replace(/[_]/g, ' ');
    }
  }

  /** @OuterBorder - set border attribute value of an outer (editable_component_container) element.
   */
  @Input() outerBorder: boolean;

  /** @setBorder - set border on either of element i.e. editable_component_container or ele_editable.
   */
  @Input() setBorder: boolean;

  /** @strPlaceHolderText - set placeholder attribute value of an editable element.
   */
  @Input() strPlaceholder: string;

  @Output() inputChanged = new EventEmitter<string>();

  @Output() keyPress = new EventEmitter<any>();

  @Input() inputId;

  inputTypeHtml;
  useDefaultAccessibility = true;

  /** @editableLabel - getter - returns EditableComponent's element.
   */
  get textbox() {
    return this.eleTextBox;
  }

  /** @eleEditableTextBox - getter - returns input element ( textbox ).
   */
  get eleEditableTextBox() {
    return this.eleEditable;
  }


  public getMinVal: any;
  public getMaxVal: any;

  /** @construtor
   * @param {ElementRef} ele - element of Editable component
   */
  constructor(private elem: ElementRef, private keyboardService: KeyboardService,
    private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) {
    super(elem);
    this.setText('');
    this.inputType = InputType.text;
    this.inputTypeHtml = this.inputType;
    this.outerBorder = true;
    this.setBorder = true;
    this.maxChar = 10;
    this.strPlaceholder = '';
  }

  /** @ngOnInit - set default values of properties on Init
   */
  ngOnInit() {
    this.focusOnPrefixClicked = true;
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  /** @onKeyPress - method - manipulate user input
   */
  onKeyPress(event: any): boolean {

    let inputcode: number = event.charCode;

    if (inputcode === 0) {
      inputcode = event.keyCode;
    }

    this.setText(event.target.value);

    this.keyPress.emit(event);

    return this.keyboardService.validateInput(
      event.key, inputcode, event.shiftKey, event.altKey, event.ctrlKey, this.inputType);

  }
  onKeyDown(event: any): boolean {
    if (KeyboardService.isIE) {
      if (event.keyCode === 38) {
        this.setText((parseInt(event.target.value, 10) + 1).toString());
        return false;
      } else if (event.keyCode === 40) {
        this.setText((parseInt(event.target.value, 10) - 1).toString());
        return false;
      }
    }
    return true;
  }


  checkInputRange(input: number, min: number, max: number): boolean {
    if ((input < min && input > min) || input > max) {
      return false;
    }
    return true;
  }


  /** @setFocus - method - set focus on Editable element ( eleEditable ).
   */
  setFocus(setFocus: boolean) {
    this.eleEditable.nativeElement.focus();
  }

  /** @setHeight - method - setHeight 2px less than specified as it takes 1 + 1 = 2 px outline.
   */
  setHeight(value: number, unit: string) {
    super.setHeight(value - 2, unit);
  }

  onChange(event) {
    if (this.inputType === InputType.number) {
      if (!this.checkInputRange(parseInt(event.target.value, 10), this.getMin(), this.getMax())) {
        event.target.value = super.getText();
      }
    }
    this.setText(event.target.value);
  }

  /** sets text and emits input changed event.
   * @param  {string} value
   */
  setText(value: string) {
    const newVal = value;

    if (value !== this.text) {
      this.text = value;

      if (this.inputType === InputType.number) {
        this.inputChanged.emit(this.getText().toString().replace(',', ''));
      } else {
        this.inputChanged.emit(value);
      }
    }
  }

  getText(): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, this.text);
  }

  setDefaultVal(value: string) {
    const newVal = value;

    if (value !== this.text) {

      this.defaultVal = value;
      this.text = value;
    }
  }

  setMax(value: number) {
    super.setMax(value);

    if (parseInt(this.text, 10) > value) {
      this.text = value.toString();
    }
  }

  setBottomBorder(bool: boolean) {
    this.hideBottomBorder = bool;
  }

  resetTextBox() {
    this.setText('');
    this.inputType = InputType.text;
    this.inputTypeHtml = this.inputType;
    this.outerBorder = true;
    this.setBorder = true;
    this.maxChar = 10;
  }

  getInputLength(): number {
    if (this.getText() === null) {
      return 0;
    }
    return this.getText().length;
  }

  checkMinLength(input: number, min: number): boolean {
    if (input < min) {
      return false;
    }
    return true;
  }

  checkMaxLength(input: number, max: number): boolean {
    if (input > max) {
      return false;
    }
    return true;
  }

}



// WEBPACK FOOTER //
// D:/Work/Biddle/biddle-training-module/biddle-training-admin/src/app/core/components/textbox/textbox.component.ts